// Maps the public-catalog course DTO (GET /api/public/catalog/courses[/:idOrSlug])
// into the shape the landing marketplace UI renders.
//
// The public API now returns the marketplace fields directly:
//   pricing, stats, ratingAverage, reviewCount, studentCount, lectureCount,
//   resourcePersons, topics, viewer, badges, learning, includes, curriculum.
// Legacy fields (price, coverImage, tutor, category, lessonCount, …) are kept
// as fallbacks for older payloads.

import { imageUrl } from "../../lib/format";

export const DIFFICULTY_LABELS = {
  BEGINNER: "Beginner",
  INTERMEDIATE: "Intermediate",
  ADVANCED: "Advanced",
  ALL_LEVELS: "All Levels",
};

/** "1 hour 46 minutes" / "2 hours" / "10 minutes 41 seconds" — full words. */
export function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  if (!seconds) return "";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  if (minutes) parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
  if (!hours && secs) parts.push(`${secs} second${secs === 1 ? "" : "s"}`);
  return parts.join(" ");
}

/** "1 hour 46 minutes" — converts a decimal hours value (1.77) to full words. */
export function formatTotalHours(hours) {
  const value = Number(hours) || 0;
  if (value <= 0) return "";
  return formatDuration(Math.round(value * 3600));
}

export function formatCount(value) {
  return (Number(value) || 0).toLocaleString();
}

/** Flatten an HTML string (course descriptions ship as rich HTML) to plain text. */
export function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

export function difficultyLabel(level) {
  if (!level) return "";
  return DIFFICULTY_LABELS[String(level).toUpperCase()] || level;
}

/** Turn a course title into a URL-safe slug: "How the Internet Works" -> "how-the-internet-works". */
export function slugify(value) {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

/** True when a route param is a course UUID. */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeTopics(api) {
  if (Array.isArray(api.topics) && api.topics.length) return api.topics;
  if (api.category?.name) {
    return [{ name: api.category.name, slug: slugify(api.category.name) }];
  }
  return [];
}

function normalizeResourcePersons(api) {
  if (Array.isArray(api.resourcePersons) && api.resourcePersons.length) {
    return api.resourcePersons;
  }
  if (api.tutor?.name) {
    return [{ id: api.tutor.id, name: api.tutor.name }];
  }
  return [];
}

export function mapPublicCourse(api = {}) {
  const pricing = api.pricing || {};
  const stats = api.stats || {};
  const viewer = api.viewer || {};
  const badges = api.badges || {};
  const includes = api.includes || {};
  const learning = api.learning || {};
  const curriculum = api.curriculum || {};

  // Legacy payloads only have `price` / `isFree` / `enrollmentCount`.
  const legacyPrice = Number(api.price) || 0;
  const price = pricing.price !== undefined ? Number(pricing.price) || 0 : legacyPrice;
  const originalPrice =
    pricing.originalPrice !== undefined ? Number(pricing.originalPrice) || 0 : 0;
  const isFree = pricing.isFree !== undefined ? Boolean(pricing.isFree) : price === 0;
  const discountPercentage = Number(pricing.discountPercentage) || 0;

  const totalDurationSeconds = Number(stats.totalDurationSeconds) || 0;
  const totalHours =
    Number(stats.totalHours) ||
    Number(api.totalHours) ||
    (totalDurationSeconds ? totalDurationSeconds / 3600 : 0);

  const lectureCount =
    Number(stats.lectureCount) || Number(api.lectureCount) || Number(api.lessonCount) || 0;
  const sectionCount = Number(stats.sectionCount) || 0;
  const hasContent = stats.hasContent !== undefined ? Boolean(stats.hasContent) : lectureCount > 0;

  const resourcePersons = normalizeResourcePersons(api);
  const topics = normalizeTopics(api);

  return {
    // ── Identity / presentation ───────────────────────────────────────────
    id: api.id,
    // Prefer the API slug; synthesize one from the title while the backend
    // still returns `slug: null` for older courses (the detail page resolves
    // it back to the course id through public search).
    slug: api.slug || slugify(api.title),
    title: api.title || "",
    summary: api.summary || "",
    description: stripHtml(api.description),
    descriptionHtml: api.description || "",
    introductionHtml: api.introduction || "",
    learningOutcomesHtml: api.learningOutcomes || "",
    requirementsHtml: api.requirements || "",
    targetAudienceHtml: api.targetAudience || "",
    image: imageUrl(api.thumbnailUrl || api.coverImage) || "",
    coverImage: imageUrl(api.thumbnailUrl || api.coverImage) || "",
    status: api.status || "",
    level: difficultyLabel(api.level || api.difficultyLevel),
    difficultyLevel: api.level || api.difficultyLevel || "",
    uploadedAt: api.uploadedAt || "",
    updatedAt: api.updatedAt || "",
    isFeatured: !!api.isFeatured,

    // ── Pricing ───────────────────────────────────────────────────────────
    pricing: {
      currency: pricing.currency || "NGN",
      price,
      originalPrice,
      discountPercentage,
      isFree,
    },
    price,
    oldPrice: originalPrice,
    isFree,
    discountPercentage,

    // ── Marketplace stats ─────────────────────────────────────────────────
    ratingAverage:
      api.ratingAverage === null || api.ratingAverage === undefined
        ? null
        : Number(api.ratingAverage),
    reviewCount: Number(api.reviewCount) || 0,
    studentCount: Number(api.studentCount ?? api.enrollmentCount) || 0,
    students: Number(api.studentCount ?? api.enrollmentCount) || 0,
    lectureCount,
    lessons: lectureCount,
    totalHours,
    durationLabel: formatDuration(totalDurationSeconds) || formatTotalHours(totalHours),
    durationDetailed: formatDuration(totalDurationSeconds),
    stats: {
      sectionCount,
      lectureCount,
      totalDurationSeconds,
      totalHours,
      hasContent,
    },

    resourcePersons,
    topics,
    viewer: {
      isAuthenticated: !!viewer.isAuthenticated,
      isWishlisted: !!viewer.isWishlisted,
      isInCart: !!viewer.isInCart,
      isEnrolled: !!viewer.isEnrolled,
    },
    badges: {
      bestseller: !!badges.bestseller,
      hot: !!badges.hot,
    },
    bestseller: !!badges.bestseller,
    hot: !!badges.hot,

    // ── Detail-only fields (list responses omit some of these) ────────────
    learning: {
      intro: stripHtml(learning.intro),
      items: Array.isArray(learning.items) ? learning.items : [],
    },
    includes: {
      hasCertificate: !!includes.hasCertificate,
      accessType: includes.accessType || "",
      moneyBackDays: Number(includes.moneyBackDays) || 0,
    },
    curriculum: {
      sections: Array.isArray(curriculum.sections) ? curriculum.sections : [],
    },

    // ── Legacy aliases used by existing components ────────────────────────
    instructor: resourcePersons[0]?.name || api.tutor?.name || "",
    tutor: api.tutor || {},
    category: api.category?.name || topics[0]?.name || "",
    categoryId: api.category?.id || "",
    enrollmentCount: api.enrollmentCount ?? 0,
  };
}
